import { useState, useEffect } from 'react';
import { api } from '../../lib/api';
import {
  UserPlusIcon,
  ShieldCheckIcon,
  TrashIcon,
  EnvelopeIcon,
} from '@heroicons/react/24/outline';

interface TeamMember {
  id: string;
  email: string;
  business_name: string;
  is_admin: boolean;
  last_login: string | null;
  created_at: string;
}

export default function AdminTeamPage() {
  const [members, setMembers] = useState<TeamMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [newEmail, setNewEmail] = useState('');
  const [newName, setNewName] = useState('');
  const [adding, setAdding] = useState(false);
  const [error, setError] = useState('');

  const loadTeam = async () => {
    try {
      const data = await api.getTeamMembers();
      setMembers(data.members);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadTeam(); }, []);

  const handleAddMember = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newEmail.trim()) return;
    setAdding(true);
    setError('');
    try {
      await api.addTeamMember(newEmail.trim(), newName.trim());
      setNewEmail('');
      setNewName('');
      setShowAddForm(false);
      await loadTeam();
    } catch (err: any) {
      setError(err.message || 'Failed to add team member');
    } finally {
      setAdding(false);
    }
  };

  const handleToggleAdmin = async (memberId: string, currentlyAdmin: boolean) => {
    if (!confirm(currentlyAdmin ? 'Remove admin access for this user?' : 'Grant admin access to this user?')) return;
    try {
      await api.toggleAdmin(memberId, !currentlyAdmin);
      await loadTeam();
    } catch (err: any) {
      alert(err.message || 'Failed to update');
    }
  };

  const handleRemove = async (memberId: string, email: string) => {
    if (!confirm(`Remove ${email} from the team? This will revoke their admin access.`)) return;
    try {
      await api.removeTeamMember(memberId);
      await loadTeam();
    } catch (err: any) {
      alert(err.message || 'Failed to remove');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-copper-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-charcoal-800">Team Management</h1>
          <p className="text-sm text-gray-500 mt-1">Manage who has admin access to Trailhead</p>
        </div>
        <button
          onClick={() => setShowAddForm(!showAddForm)}
          className="flex items-center justify-center gap-2 px-4 py-2.5 bg-copper-500 text-white text-sm font-semibold rounded-lg hover:bg-copper-600 transition-colors"
        >
          <UserPlusIcon className="h-4 w-4" />
          Add Team Member
        </button>
      </div>

      {showAddForm && (
        <div className="bg-white rounded-xl border border-gray-200 p-4 sm:p-6">
          <h3 className="font-semibold text-charcoal-800 mb-4">Add New Admin</h3>
          <form onSubmit={handleAddMember} className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-600 mb-1">Email Address</label>
                <input type="email" value={newEmail} onChange={e => setNewEmail(e.target.value)}
                  placeholder="team@company.com" required
                  className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-copper-500 focus:border-transparent" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-600 mb-1">Display Name</label>
                <input type="text" value={newName} onChange={e => setNewName(e.target.value)}
                  placeholder="Jane Smith"
                  className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-copper-500 focus:border-transparent" />
              </div>
            </div>
            {error && <p className="text-sm text-red-600">{error}</p>}
            <p className="text-xs text-gray-400">They'll be able to log in with a magic link sent to this email.</p>
            <div className="flex flex-wrap gap-3">
              <button type="submit" disabled={adding}
                className="px-4 py-2.5 bg-copper-500 text-white text-sm font-semibold rounded-lg hover:bg-copper-600 disabled:opacity-50 transition-colors">
                {adding ? 'Adding...' : 'Add Admin'}
              </button>
              <button type="button" onClick={() => { setShowAddForm(false); setError(''); }}
                className="px-4 py-2.5 border border-gray-200 text-gray-600 text-sm font-medium rounded-lg hover:bg-gray-50 transition-colors">
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="p-4 border-b border-gray-200">
          <h2 className="font-semibold text-charcoal-800">Team Members</h2>
        </div>
        <div className="divide-y divide-gray-100">
          {members.map((member) => (
            <div key={member.id} className="px-4 sm:px-5 py-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3 hover:bg-gray-50 transition-colors">
              <div className="flex items-center gap-3 min-w-0">
                <div className="w-10 h-10 rounded-full bg-teal-100 flex items-center justify-center text-teal-600 font-bold text-sm flex-shrink-0">
                  {(member.business_name || member.email)[0].toUpperCase()}
                </div>
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-medium text-charcoal-800">{member.business_name || 'Unnamed'}</span>
                    {member.is_admin && (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-teal-100 text-teal-700">
                        <ShieldCheckIcon className="h-3 w-3" /> Admin
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-1 text-sm text-gray-500">
                    <EnvelopeIcon className="h-3.5 w-3.5 flex-shrink-0" />
                    <span className="truncate">{member.email}</span>
                  </div>
                  <p className="text-xs text-gray-400 mt-0.5">
                    {member.last_login ? `Last login: ${new Date(member.last_login).toLocaleDateString()}` : 'Never logged in'}
                    {' · '}Added {new Date(member.created_at).toLocaleDateString()}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0 ml-13 sm:ml-0">
                <button
                  onClick={() => handleToggleAdmin(member.id, member.is_admin)}
                  className={`px-3 py-1.5 text-xs font-medium rounded-lg border transition-colors whitespace-nowrap ${
                    member.is_admin
                      ? 'text-orange-600 border-orange-200 hover:bg-orange-50'
                      : 'text-teal-600 border-teal-200 hover:bg-teal-50'
                  }`}
                >
                  {member.is_admin ? 'Remove Admin' : 'Make Admin'}
                </button>
                <button
                  onClick={() => handleRemove(member.id, member.email)}
                  className="p-1.5 text-gray-400 hover:text-red-500 transition-colors"
                  title="Remove from team"
                >
                  <TrashIcon className="h-4 w-4" />
                </button>
              </div>
            </div>
          ))}
          {members.length === 0 && (
            <div className="text-center py-12 text-gray-400">No team members yet.</div>
          )}
        </div>
      </div>
    </div>
  );
}
